import React, { useState, useEffect } from 'react';
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';
import { Menu, X, ChevronRight, ChevronDown } from 'lucide-react';
import Breadcrumbs from './components/Breadcrumbs';
import StickyCTABar from './components/StickyCTABar';

/* -------------------------------------------------------------------------- */
/* Nav: Solutions mega menu + primary links                                   */
/* -------------------------------------------------------------------------- */

const SOLUTIONS = {
  'Sector Platforms': [
    { label: 'Videra Healthcare', to: '/videra/healthcare', note: 'NHS England / Scotland' },
    { label: 'Videra Hard Services', to: '/videra/hard-services', note: 'PFI-grade lifecycle & projects' },
    { label: 'Videra Construction', to: '/videra/construction', note: 'Governed delivery' },
  ],
  'Create Your Own': [
    { label: 'Videra PM', to: '/videra/pm', note: 'Configurable, pick your sector' },
  ],
  'PFI / P3 & FM': [
    { label: 'Videra Vanguard', to: '/videra/vanguard', note: 'Contract & asset assurance' },
    { label: 'Handback Readiness', to: '/handback-readiness', note: 'Continuous, not last-minute' },
    { label: 'International P3 / PPP', to: '/international-p3', note: 'UK experience, local model' },
  ],
  'Firestopping': [
    { label: 'SafeTrace', to: '/firestopping/safetrace', note: 'Survey → remediate → verify' },
    { label: 'Firestopping Business Dashboard', to: '/firestopping/business-dashboard', note: 'Run the whole business' },
  ],
  'Bespoke': [
    { label: 'Keystone + Videra', to: '/consultancy', note: 'Diagnose, design, configure' },
  ],
};

const PRIMARY_LINKS = [
  { name: 'Home', path: '/', end: true },
  { name: 'Solutions', path: '/solutions', mega: true },
  { name: 'Industries', path: '/industries' },
  { name: 'Clients', path: '/clients' },
  { name: 'Insights', path: '/insights' },
  { name: 'About', path: '/about' },
];

const V2Nav = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [megaOpen, setMegaOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setIsScrolled(window.scrollY > 12);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Close mega menu on route change
  useEffect(() => { setMegaOpen(false); setOpen(false); }, [location.pathname]);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-40 transition-all duration-300 ${
        isScrolled ? 'bg-white/90 backdrop-blur-xl border-b border-slate-200/60 shadow-sm' : 'bg-white/70 backdrop-blur'
      }`}
      data-testid="v2-nav"
    >
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          <Link to="/" className="flex items-center" data-testid="v2-nav-logo">
            <div className="leading-tight">
              <div className="text-xl font-semibold text-slate-900 tracking-tight">Keystone</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-medium mt-0.5">Strategic Consultants</div>
            </div>
          </Link>

          <div className="hidden lg:flex items-center gap-7">
            {PRIMARY_LINKS.map((l) => {
              if (l.mega) {
                return (
                  <button
                    key={l.name}
                    onClick={() => setMegaOpen((v) => !v)}
                    onMouseEnter={() => setMegaOpen(true)}
                    className={`inline-flex items-center gap-1 text-sm font-medium transition-colors ${
                      megaOpen ? 'text-slate-900' : 'text-slate-600 hover:text-slate-900'
                    }`}
                    data-testid="v2-nav-solutions-toggle"
                  >
                    {l.name}
                    <ChevronDown className={`w-3.5 h-3.5 transition-transform ${megaOpen ? 'rotate-180' : ''}`} />
                  </button>
                );
              }
              return (
                <NavLink
                  key={l.path}
                  to={l.path}
                  end={l.end}
                  data-testid={`v2-nav-link-${l.name.toLowerCase().replace(/\s+/g, '-')}`}
                  className={({ isActive }) =>
                    `text-sm font-medium transition-colors relative ${
                      isActive ? 'text-slate-900' : 'text-slate-600 hover:text-slate-900'
                    }`
                  }
                >
                  {({ isActive }) => (
                    <>
                      {l.name}
                      {isActive && <span className="absolute -bottom-1.5 left-0 right-0 h-0.5 bg-blue-600" />}
                    </>
                  )}
                </NavLink>
              );
            })}
          </div>

          <div className="hidden lg:flex items-center gap-3">
            <Link
              to="/contact"
              data-testid="v2-nav-talk"
              className="text-sm font-medium text-slate-900 hover:text-blue-600 transition-colors"
            >
              Talk to Keystone
            </Link>
            <Link
              to="/?try=1"
              data-testid="v2-nav-cta"
              className="inline-flex items-center gap-2 bg-slate-900 text-white px-5 py-2.5 rounded-md text-sm font-medium hover:bg-slate-800 transition-colors"
            >
              Try Videra
              <ChevronRight className="w-4 h-4" />
            </Link>
          </div>

          <button
            className="lg:hidden p-2"
            onClick={() => setOpen(!open)}
            data-testid="v2-mobile-toggle"
            aria-label="Toggle menu"
          >
            {open ? <X className="w-6 h-6 text-slate-900" /> : <Menu className="w-6 h-6 text-slate-900" />}
          </button>
        </div>

        {/* Mega menu */}
        {megaOpen && (
          <div
            onMouseLeave={() => setMegaOpen(false)}
            className="hidden lg:block absolute left-0 right-0 top-full bg-white border-t border-slate-200 shadow-lg"
            data-testid="v2-nav-mega"
          >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
              <div className="grid grid-cols-5 gap-8">
                {Object.entries(SOLUTIONS).map(([group, items]) => (
                  <div key={group}>
                    <p className="text-[11px] uppercase tracking-[0.22em] text-slate-500 font-semibold mb-4">{group}</p>
                    <ul className="space-y-3">
                      {items.map((it) => (
                        <li key={it.to}>
                          <Link
                            to={it.to}
                            className="block group"
                            data-testid={`v2-mega-link-${it.to.replace(/\W/g, '-')}`}
                          >
                            <p className="text-sm font-medium text-slate-900 group-hover:text-blue-600 transition-colors">{it.label}</p>
                            <p className="text-xs text-slate-500 mt-0.5">{it.note}</p>
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Mobile menu */}
        {open && (
          <div className="lg:hidden border-t border-slate-200 py-4 space-y-1 max-h-[75vh] overflow-y-auto" data-testid="v2-mobile-menu">
            {PRIMARY_LINKS.filter((l) => !l.mega).map((l) => (
              <NavLink
                key={l.path}
                to={l.path}
                end={l.end}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `block px-4 py-2 text-base font-medium rounded ${
                    isActive ? 'text-slate-900 bg-slate-50' : 'text-slate-700 hover:bg-slate-50'
                  }`
                }
              >
                {l.name}
              </NavLink>
            ))}
            <div className="border-t border-slate-100 mt-2 pt-2">
              <p className="px-4 py-2 text-[11px] uppercase tracking-[0.22em] text-slate-500 font-semibold">Solutions</p>
              {Object.values(SOLUTIONS).flat().map((it) => (
                <Link
                  key={it.to}
                  to={it.to}
                  onClick={() => setOpen(false)}
                  className="block px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 rounded"
                >
                  {it.label}
                </Link>
              ))}
            </div>
            <div className="px-4 pt-3 space-y-2">
              <Link
                to="/contact"
                onClick={() => setOpen(false)}
                className="block w-full text-center px-5 py-3 rounded-md text-sm font-medium border border-slate-300 text-slate-900"
              >
                Talk to Keystone
              </Link>
              <Link
                to="/?try=1"
                onClick={() => setOpen(false)}
                className="block w-full bg-slate-900 text-white text-center px-5 py-3 rounded-md text-sm font-medium"
              >
                Try Videra
              </Link>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
};

/* -------------------------------------------------------------------------- */
/* Footer                                                                     */
/* -------------------------------------------------------------------------- */

const V2Footer = () => (
  <footer className="bg-slate-950 text-slate-300 py-16" data-testid="v2-footer">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="grid grid-cols-1 md:grid-cols-5 gap-10 mb-12">
        <div className="md:col-span-1">
          <div className="mb-4">
            <div className="text-xl font-semibold text-white tracking-tight">Keystone</div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-medium mt-0.5">Strategic Consultants</div>
          </div>
          <p className="text-sm text-slate-400 leading-relaxed">
            The experience behind Videra.<br />
            Built from experience. Designed around your world.
          </p>
        </div>

        <div>
          <p className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold mb-4">Keystone</p>
          <ul className="space-y-2 text-sm">
            <li><Link to="/about" className="hover:text-white transition-colors">About</Link></li>
            <li><Link to="/consultancy" className="hover:text-white transition-colors">Consultancy</Link></li>
            <li><Link to="/clients" className="hover:text-white transition-colors">Clients</Link></li>
            <li><Link to="/contact" className="hover:text-white transition-colors">Contact</Link></li>
            <li><Link to="/insights" className="hover:text-white transition-colors">Insights</Link></li>
            <li><Link to="/investors" className="hover:text-white transition-colors" data-testid="v2-footer-investors">Investors</Link></li>
          </ul>
        </div>

        <div>
          <p className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold mb-4">Videra</p>
          <ul className="space-y-2 text-sm">
            <li><Link to="/videra/healthcare" className="hover:text-white transition-colors">Healthcare</Link></li>
            <li><Link to="/videra/hard-services" className="hover:text-white transition-colors">Hard Services</Link></li>
            <li><Link to="/videra/construction" className="hover:text-white transition-colors">Construction</Link></li>
            <li><Link to="/videra/pm" className="hover:text-white transition-colors">Videra PM</Link></li>
            <li><Link to="/videra/vanguard" className="hover:text-white transition-colors">Videra Vanguard</Link></li>
            <li><Link to="/handback-readiness" className="hover:text-white transition-colors">Handback Readiness</Link></li>
            <li><Link to="/international-p3" className="hover:text-white transition-colors">International P3 / PPP</Link></li>
          </ul>
        </div>

        <div>
          <p className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold mb-4">Firestopping</p>
          <ul className="space-y-2 text-sm">
            <li><Link to="/firestopping/safetrace" className="hover:text-white transition-colors">SafeTrace</Link></li>
            <li><Link to="/firestopping/business-dashboard" className="hover:text-white transition-colors">Business Dashboard</Link></li>
          </ul>
        </div>

        <div>
          <p className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold mb-4">Experience</p>
          <ul className="space-y-2 text-sm">
            <li><a href="https://demo.videra.app/explore" className="hover:text-white transition-colors">Try Videra</a></li>
            <li><a href="https://pm.videra.app" className="hover:text-white transition-colors">Videra PM</a></li>
            <li><Link to="/contact" className="hover:text-white transition-colors">Contact</Link></li>
          </ul>
        </div>
      </div>

      <div className="border-t border-slate-800 pt-8 flex flex-col sm:flex-row justify-between gap-4 text-xs text-slate-500">
        <p>© {new Date().getFullYear()} Keystone Strategic Consultants. All rights reserved.</p>
        <p className="flex items-center gap-4">
          <span>Projects · Lifecycle · Assurance · Evidence</span>
          <a
            href="/admin"
            className="text-slate-600 hover:text-slate-400 transition-colors"
            data-testid="v2-footer-admin-link"
          >
            Admin
          </a>
        </p>
      </div>
    </div>
  </footer>
);

const V2Layout = () => {
  const location = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [location.pathname]);

  // demo.videra.app links open in a new tab with a back-URL param so the
  // demo app can show a "back to Keystone" banner.
  useEffect(() => {
    const handler = (e) => {
      if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      const anchor = e.target.closest && e.target.closest('a[href*="demo.videra.app"], a[href*="pm.videra.app"]');
      if (!anchor) return;
      e.preventDefault();
      const href = anchor.getAttribute('href');
      const sep = href.includes('?') ? '&' : '?';
      const back = encodeURIComponent(window.location.href);
      const target = `${href}${sep}from=keystone&back=${back}`;
      window.open(target, '_blank', 'noopener,noreferrer');
    };
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, []);

  return (
    <div className="min-h-screen bg-white overflow-x-hidden" data-testid="v2-layout">
      <V2Nav />
      <div className="pt-20">
        <Breadcrumbs />
        <main>
          <Outlet />
        </main>
      </div>
      <V2Footer />
      <StickyCTABar />
    </div>
  );
};

export default V2Layout;
